// client/src/components/AdminAccount.js
// Organization management page for admins and superadmins

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { HeadlineMedium, LabelMedium, BodyMedium } from "./Typography";
import {
  fetchOrganization,
  updateOrganization,
  createOrganization,
} from "../utils/organizationUtils";
import {
  fetchManageableUsers,
  validateUserAction,
} from "../utils/orgDataUtils";
import { canManageUser, isAdmin, isSuperAdmin } from "../utils/roleUtils";
import { getAdminUrl } from "../utils/urlUtils";
import Toast from "./Toast";
import { apiFetch } from "../services/apiClient";
import AnalyticsReportingService from "../services/analyticsReportingService";
import { db } from "../firebase";
import { doc, updateDoc } from "firebase/firestore";

/** Parse comma-separated ID prefix input into a trimmed, non-empty array. */
const parseIdPrefixes = (value) =>
  String(value ?? "")
    .split(",")
    .map((prefix) => prefix.trim())
    .filter((prefix) => prefix.length > 0);

const ACCESS_FIELD_HELP = {
  idPrefixes: {
    title: "Library card / student ID prefix",
    content:
      "Enter the starting characters that valid IDs must begin with when patrons sign up. Use commas between multiple prefixes (e.g. 1234,5678). Leave blank to skip the ID question on sign-up and registration.",
  },
  registrationLabel: {
    title: "Label for registration page",
    content:
      "This text appears above the ID field on sign-up and registration when ID prefixes are configured. Examples: Library Card Number, Student ID, or Patron ID.",
  },
  branding: {
    title: "Branding (optional)",
    content: "Customize appearance for this library's patrons.",
  },
};

const FieldHelpButton = ({ label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] shrink-0 text-gray-500 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-full"
    aria-label={`Help: ${label}`}
  >
    <svg
      className="w-5 h-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" strokeWidth="2" />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3m.08 4h.01"
      />
    </svg>
  </button>
);

const AccessSection = ({
  formData,
  onInputChange,
  onSave,
  saving,
  signupLink,
  onCopySignupLink,
}) => {
  const [fieldHelpDialog, setFieldHelpDialog] = useState(null);
  const fieldHelpDialogTitleRef = useRef(null);

  const openFieldHelpDialog = useCallback((helpKey) => {
    setFieldHelpDialog(ACCESS_FIELD_HELP[helpKey]);
  }, []);

  const closeFieldHelpDialog = useCallback(() => {
    setFieldHelpDialog(null);
  }, []);

  useEffect(() => {
    if (fieldHelpDialog && fieldHelpDialogTitleRef.current) {
      fieldHelpDialogTitleRef.current.focus();
    }
  }, [fieldHelpDialog]);

  useEffect(() => {
    if (!fieldHelpDialog) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeFieldHelpDialog();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [fieldHelpDialog, closeFieldHelpDialog]);

  const saveButtonText = saving ? "Saving..." : "Save Access Settings";

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-6">
        <HeadlineMedium color="gray-900">Access</HeadlineMedium>
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors min-h-[44px]"
        >
          {saveButtonText}
        </button>
      </div>

      <div className="space-y-6">
        <div>
          <div className="flex items-center gap-1">
            <label htmlFor="access-id-prefixes" className="block">
              <LabelMedium color="gray-700">
                Library card / student ID prefix
              </LabelMedium>
            </label>
            <FieldHelpButton
              label={ACCESS_FIELD_HELP.idPrefixes.title}
              onClick={() => openFieldHelpDialog("idPrefixes")}
            />
          </div>
          <input
            id="access-id-prefixes"
            type="text"
            value={formData.idPrefixes}
            onChange={(e) => onInputChange("idPrefixes", e.target.value)}
            className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]"
            placeholder="1234,5678"
          />
        </div>

        <div>
          <div className="flex items-center gap-1">
            <label htmlFor="access-registration-label" className="block">
              <LabelMedium color="gray-700">
                Label for registration page
              </LabelMedium>
            </label>
            <FieldHelpButton
              label={ACCESS_FIELD_HELP.registrationLabel.title}
              onClick={() => openFieldHelpDialog("registrationLabel")}
            />
          </div>
          <input
            id="access-registration-label"
            type="text"
            value={formData.registrationLabel}
            onChange={(e) =>
              onInputChange("registrationLabel", e.target.value)
            }
            className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]"
            placeholder="Library Card Number"
          />
        </div>

        <div>
          <div className="flex items-center gap-1 mb-2">
            <LabelMedium color="gray-700">Branding (optional)</LabelMedium>
            <FieldHelpButton
              label={ACCESS_FIELD_HELP.branding.title}
              onClick={() => openFieldHelpDialog("branding")}
            />
          </div>
          <div className="space-y-3">
            <input
              type="text"
              value={formData.appDisplayName}
              onChange={(e) => onInputChange("appDisplayName", e.target.value)}
              className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="App display name"
              aria-label="App display name"
            />
            <input
              type="url"
              value={formData.logoUrl}
              onChange={(e) => onInputChange("logoUrl", e.target.value)}
              className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Logo URL"
              aria-label="Logo URL"
            />
            <input
              type="text"
              value={formData.primaryColor}
              onChange={(e) => onInputChange("primaryColor", e.target.value)}
              className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Primary color (e.g. #0A4872)"
              aria-label="Primary brand color"
            />
          </div>
        </div>

        <div>
          <LabelMedium color="gray-700">
            This is the link you&apos;ll use to provide access to your
            community:
          </LabelMedium>
          <div className="mt-2 flex items-center space-x-2">
            <input
              type="text"
              value={signupLink}
              readOnly
              className="flex-1 bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-gray-600 min-h-[44px]"
              aria-label="Community signup link"
            />
            <button
              type="button"
              onClick={onCopySignupLink}
              className="min-h-[44px] min-w-[44px] px-3 py-2 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              aria-label="Copy signup link"
            >
              <svg
                className="w-4 h-4 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {fieldHelpDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={closeFieldHelpDialog}
          role="presentation"
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="access-field-help-dialog-title"
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2
              id="access-field-help-dialog-title"
              tabIndex={-1}
              ref={fieldHelpDialogTitleRef}
              className="text-lg font-semibold text-gray-900 mb-3 outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded"
            >
              {fieldHelpDialog.title}
            </h2>
            <BodyMedium color="gray-700" className="mb-6">
              {fieldHelpDialog.content}
            </BodyMedium>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={closeFieldHelpDialog}
                className="inline-flex items-center justify-center min-h-[44px] px-4 rounded-md border border-transparent bg-blue-600 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminAccount = ({
  organizationId: propOrgId,
  mode = "admin",
  onBackToOrganizations,
}) => {
  const { user, userRole, userOrganizations } = useAuth();
  const { orgId: urlOrgId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine organization ID: URL param takes precedence over prop
  // Normalize "new" to null for new organization creation
  const rawOrgId = urlOrgId || propOrgId;
  const organizationId = rawOrgId === "new" ? null : rawOrgId;
  const [organization, setOrganization] = useState(null);
  const [adminUsers, setAdminUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Toast notification state
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");

  // Admin invitation state
  const [showAddAdminModal, setShowAddAdminModal] = useState(false);
  const [adminFormData, setAdminFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });
  const [invitingAdmin, setInvitingAdmin] = useState(false);

  // Edit / remove administrator state
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [editFormData, setEditFormData] = useState({
    firstName: "",
    lastName: "",
  });
  const [savingAdminEdit, setSavingAdminEdit] = useState(false);
  const [resendingAdminInvite, setResendingAdminInvite] = useState(false);
  const [removingAdminId, setRemovingAdminId] = useState(null);

  // Toast helper functions
  const showToast = (message, type = "success") => {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  };

  const hideToast = () => {
    setToastVisible(false);
  };

  // Admin invitation handlers
  const handleAddAdminClick = () => {
    setShowAddAdminModal(true);
  };

  const handleCloseAdminModal = () => {
    setShowAddAdminModal(false);
    setAdminFormData({
      firstName: "",
      lastName: "",
      email: "",
    });
  };

  const handleAdminFormChange = (field, value) => {
    setAdminFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleInviteAdmin = async (e) => {
    e.preventDefault();

    if (
      !adminFormData.firstName.trim() ||
      !adminFormData.lastName.trim() ||
      !adminFormData.email.trim()
    ) {
      showToast("Please fill in all fields", "error");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(adminFormData.email)) {
      showToast("Please enter a valid email address", "error");
      return;
    }

    setInvitingAdmin(true);

    try {
      const data = await apiFetch("/api/admin/invite", {
        method: "POST",
        authenticated: true,
        body: JSON.stringify({
          organizationId,
          firstName: adminFormData.firstName.trim(),
          lastName: adminFormData.lastName.trim(),
          email: adminFormData.email.trim().toLowerCase(),
        }),
      });

      if (data.success) {
        showToast(`Invitation sent to ${adminFormData.email}`, "success");
        handleCloseAdminModal();
        // Refresh admin users list
        await loadAdminUsers();
      } else {
        // Server may return errorCode (e.g. auth/invalid-continue-uri, email_send_failed) for production debugging
        const msg = data.errorCode
          ? `${data.error || "Failed to send invitation"} (${data.errorCode}${data.errorName ? `, ${data.errorName}` : ""})`
          : data.error || "Failed to send invitation";
        showToast(msg, "error");
      }
    } catch (error) {
      console.error("Error inviting admin:", error);
      const msg = error.errorCode
        ? `${error.message} (${error.errorCode})`
        : error.message || "Failed to send invitation. Please try again.";
      showToast(msg, "error");
    } finally {
      setInvitingAdmin(false);
    }
  };

  const handleEditAdminClick = (admin) => {
    if (!isSuperAdmin(userRole)) {
      showToast("Only superadmins can edit administrator accounts.", "error");
      return;
    }

    if (!canManageUser(userRole, userOrganizations, admin)) {
      showToast(
        "You do not have permission to edit this administrator.",
        "error",
      );
      return;
    }

    const nameParts = (admin.display_name || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    setEditingAdmin(admin);
    setEditFormData({
      firstName: nameParts[0] || admin.email?.split("@")[0] || "",
      lastName: nameParts.slice(1).join(" "),
    });
  };

  const handleCloseEditAdminModal = () => {
    setEditingAdmin(null);
    setEditFormData({ firstName: "", lastName: "" });
    setResendingAdminInvite(false);
  };

  const handleEditFormChange = (field, value) => {
    setEditFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveAdminEdit = async (e) => {
    e.preventDefault();
    if (!editingAdmin) return;

    if (!isSuperAdmin(userRole)) {
      showToast("Only superadmins can edit administrator accounts.", "error");
      return;
    }

    const firstName = editFormData.firstName.trim();
    const lastName = editFormData.lastName.trim();
    if (!firstName || !lastName) {
      showToast("First and last name are required.", "error");
      return;
    }

    if (
      !validateUserAction(userRole, userOrganizations, editingAdmin, "edit")
    ) {
      showToast(
        "You do not have permission to edit this administrator.",
        "error",
      );
      return;
    }

    setSavingAdminEdit(true);
    try {
      await updateDoc(doc(db, "users", editingAdmin.id), {
        display_name: `${firstName} ${lastName}`,
        updatedAt: new Date(),
      });
      showToast("Administrator updated successfully.", "success");
      handleCloseEditAdminModal();
      await loadAdminUsers();
    } catch (err) {
      console.error("Error updating administrator:", err);
      showToast("Failed to update administrator. Please try again.", "error");
    } finally {
      setSavingAdminEdit(false);
    }
  };

  const handleResendAdminInvite = async () => {
    if (!editingAdmin) return;

    if (!isSuperAdmin(userRole)) {
      showToast(
        "Only superadmins can resend administrator invitations.",
        "error",
      );
      return;
    }

    const firstName = editFormData.firstName.trim();
    const lastName = editFormData.lastName.trim();
    if (!firstName || !lastName) {
      showToast(
        "First and last name are required before resending an invitation.",
        "error",
      );
      return;
    }

    if (
      !validateUserAction(userRole, userOrganizations, editingAdmin, "edit")
    ) {
      showToast(
        "You do not have permission to resend this invitation.",
        "error",
      );
      return;
    }

    setResendingAdminInvite(true);
    try {
      const data = await apiFetch("/api/admin/invite/resend", {
        method: "POST",
        authenticated: true,
        body: JSON.stringify({
          organizationId,
          firstName,
          lastName,
          email: editingAdmin.email,
        }),
      });

      if (data.success) {
        showToast(`Invitation resent to ${editingAdmin.email}`, "success");
        await loadAdminUsers();
      } else {
        const msg = data.errorCode
          ? `${data.error || "Failed to resend invitation"} (${data.errorCode})`
          : data.error || "Failed to resend invitation";
        showToast(msg, "error");
      }
    } catch (err) {
      console.error("Error resending admin invitation:", err);
      const msg = err.errorCode
        ? `${err.message} (${err.errorCode})`
        : err.message || "Failed to resend invitation. Please try again.";
      showToast(msg, "error");
    } finally {
      setResendingAdminInvite(false);
    }
  };

  const handleRemoveAdmin = async (admin) => {
    if (!isSuperAdmin(userRole)) {
      showToast("Only superadmins can remove administrator accounts.", "error");
      return;
    }

    if (!canManageUser(userRole, userOrganizations, admin)) {
      showToast(
        "You do not have permission to remove this administrator.",
        "error",
      );
      return;
    }

    if (user?.uid === admin.id) {
      showToast(
        "You cannot remove your own administrator access here.",
        "error",
      );
      return;
    }

    if (admin.role === "superadmin") {
      showToast(
        "Superadmin accounts are managed from the Superadmins page.",
        "error",
      );
      return;
    }

    const orgAdminsOnly = adminUsers.filter((a) => a.role === "admin");
    if (orgAdminsOnly.length <= 1 && admin.role === "admin") {
      showToast(
        "Each organization must have at least one administrator.",
        "error",
      );
      return;
    }

    const displayName = admin.display_name || admin.email;
    if (
      !window.confirm(
        `Remove administrator access for ${displayName}? They will become a student in this organization.`,
      )
    ) {
      return;
    }

    setRemovingAdminId(admin.id);
    try {
      await apiFetch("/api/admin/change-role", {
        method: "POST",
        authenticated: true,
        body: JSON.stringify({
          userId: admin.id,
          organizationId,
          newRole: "student",
        }),
      });
      showToast(
        `${displayName} is no longer an administrator for this organization.`,
        "success",
      );
      await loadAdminUsers();
    } catch (err) {
      console.error("Error removing administrator:", err);
      showToast(
        err.message || "Failed to remove administrator. Please try again.",
        "error",
      );
    } finally {
      setRemovingAdminId(null);
    }
  };

  // Load admin users for the organization
  const loadAdminUsers = async () => {
    if (!organizationId) return;

    try {
      if (isSuperAdminUser) {
        const users = await fetchManageableUsers("superadmin", [
          organizationId,
        ]);
        const orgAdmins = users.filter(
          (u) =>
            u.organizationIds?.includes(organizationId) &&
            (u.role === "admin" || u.role === "superadmin"),
        );
        setAdminUsers(orgAdmins);
      } else {
        // Server API — Firestore client queries on users/ fail for regular admins
        const userStats =
          await AnalyticsReportingService.getUserActivitySummary(
            organizationId,
          );
        const orgAdmins = userStats
          .filter((row) => row.role === "admin" || row.role === "superadmin")
          .map((row) => ({
            id: row.userId,
            email: row.email || "",
            display_name: row.displayName || row.display_name || "",
            role: row.role,
            organizationIds: [organizationId],
          }));
        setAdminUsers(orgAdmins);
      }
    } catch (error) {
      console.error("Error loading admin users:", error);
      showToast("Failed to load administrators", "error");
    }
  };

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    street: "",
    city: "",
    zipCode: "",
    state: "",
    annualSubscription: "",
    subscriptionRenewal: "",
    idPrefixes: "",
    registrationLabel: "",
    logoUrl: "",
    primaryColor: "",
    appDisplayName: "",
  });

  // Superadmins can edit org details; regular admins have read-only org info
  const isSuperAdminUser = isSuperAdmin(userRole);
  const canEditOrgDetails = isSuperAdminUser;

  // Load organization data
  useEffect(() => {
    const loadOrganizationData = async () => {
      // If no organizationId and not superadmin mode, return
      if (!organizationId && !isSuperAdminUser) {
        return;
      }

      try {
        setLoading(true);
        setError("");

        if (organizationId) {
          // Fetch existing organization details
          const orgData = await fetchOrganization(organizationId);
          if (orgData) {
            setOrganization(orgData);
            setFormData({
              name: orgData.name || "",
              street: orgData.street || "",
              city: orgData.city || "",
              zipCode: orgData.zipCode || "",
              state: orgData.state || "",
              annualSubscription: orgData.annualSubscription || "",
              subscriptionRenewal: orgData.subscriptionRenewal || "",
              idPrefixes: (orgData.access?.idPrefixes || []).join(","),
              registrationLabel: orgData.access?.registrationLabel || "",
              logoUrl: orgData.branding?.logoUrl || "",
              primaryColor: orgData.branding?.primaryColor || "",
              appDisplayName: orgData.branding?.appDisplayName || "",
            });
          } else {
            showToast("Organization not found", "error");
          }

          await loadAdminUsers();
        } else {
          // Creating new organization - set default values (must match initial formData shape)
          setFormData({
            name: "",
            street: "",
            city: "",
            state: "",
            zipCode: "",
            annualSubscription: "",
            subscriptionRenewal: "",
            idPrefixes: "",
            registrationLabel: "Student ID",
            logoUrl: "",
            primaryColor: "",
            appDisplayName: "",
          });
          setAdminUsers([]);
        }
      } catch (error) {
        console.error("Error loading organization data:", error);
        showToast("Failed to load organization data", "error");
      } finally {
        setLoading(false);
      }
    };

    loadOrganizationData();
  }, [organizationId, userRole, userOrganizations, isSuperAdminUser]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setError("");
    setSuccess("");
  };

  const handleSaveOrganization = async () => {
    try {
      setSaving(true);
      setError("");
      setSuccess("");

      if (!formData.name.trim()) {
        showToast("Organization name is required", "error");
        return;
      }

      if (!formData.city.trim()) {
        showToast("City is required", "error");
        return;
      }

      if (!formData.state.trim()) {
        showToast("State is required", "error");
        return;
      }

      const orgData = {
        name: formData.name.trim(),
        street: formData.street.trim(),
        city: formData.city.trim(),
        zipCode: formData.zipCode.trim(),
        state: formData.state.trim(),
        annualSubscription: formData.annualSubscription.trim(),
        subscriptionRenewal: formData.subscriptionRenewal.trim(),
        access: {
          idPrefixes: parseIdPrefixes(formData.idPrefixes),
          registrationLabel: formData.registrationLabel.trim() || "Student ID",
        },
        branding: {
          logoUrl: (formData.logoUrl || "").trim(),
          primaryColor: (formData.primaryColor || "").trim(),
          appDisplayName: (formData.appDisplayName || "").trim(),
        },
        isActive: true,
        updatedAt: new Date().toISOString(),
      };

      if (organizationId) {
        await updateOrganization(organizationId, orgData);
        showToast("Organization updated successfully!", "success");
        const updatedOrg = await fetchOrganization(organizationId);
        setOrganization(updatedOrg);
      } else {
        orgData.createdAt = new Date().toISOString();
        const newOrgId = await createOrganization(orgData);
        showToast("Organization created successfully!", "success");
        setOrganization({ id: newOrgId, ...orgData });
      }
    } catch (error) {
      console.error("Error saving organization:", error);
      showToast("Failed to save organization", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAccess = async () => {
    if (!organizationId) return;

    try {
      setSaving(true);
      const orgData = {
        access: {
          idPrefixes: parseIdPrefixes(formData.idPrefixes),
          registrationLabel: formData.registrationLabel.trim() || "Student ID",
        },
        branding: {
          logoUrl: (formData.logoUrl || "").trim(),
          primaryColor: (formData.primaryColor || "").trim(),
          appDisplayName: (formData.appDisplayName || "").trim(),
        },
      };

      await updateOrganization(organizationId, orgData);
      showToast("Access settings saved successfully!", "success");
      const updatedOrg = await fetchOrganization(organizationId);
      setOrganization(updatedOrg);
    } catch (error) {
      console.error("Error saving access settings:", error);
      showToast("Failed to save access settings", "error");
    } finally {
      setSaving(false);
    }
  };

  const ReadOnlyField = ({ label, value, id, className = "" }) => {
    const fieldId =
      id || `org-field-${label.replace(/\s+/g, "-").toLowerCase()}`;
    const trimmed = typeof value === "string" ? value.trim() : value;
    const isEmpty = !trimmed;

    return (
      <div className={className}>
        <label htmlFor={fieldId} className="block mb-1.5">
          <LabelMedium color="gray-700">{label}</LabelMedium>
        </label>
        <div
          id={fieldId}
          className={`flex items-center w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2.5 min-h-[44px] ${
            isEmpty ? "text-gray-400" : "text-gray-900"
          }`}
          aria-readonly="true"
        >
          <BodyMedium
            color={isEmpty ? "gray-400" : "gray-900"}
            className="block"
          >
            {isEmpty ? "—" : trimmed}
          </BodyMedium>
        </div>
      </div>
    );
  };

  const generateSignupLink = () => {
    if (!organizationId) return "Link will be available after saving";
    const baseUrl = window.location.origin;
    return `${baseUrl}/sign-in?system=${organizationId}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading organization...</span>
      </div>
    );
  }

  // For regular admins, check if they have access to the organization
  if (!isSuperAdminUser && organizationId && !organization) {
    return (
      <div className="text-center py-12">
        <BodyMedium color="red-600">
          Organization not found or access denied
        </BodyMedium>
      </div>
    );
  }

  const saveButtonText = saving
    ? "Saving..."
    : organizationId
      ? "Save Changes"
      : "Create Organization";

  const handleCopySignupLink = () => {
    navigator.clipboard.writeText(generateSignupLink());
    showToast("Signup link copied to clipboard!", "success");
  };

  return (
    <div className="space-y-8">
      {/* Toast Notification */}
      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={toastVisible}
        onClose={hideToast}
        duration={3000}
      />

      {/* Back Button for Superadmin Mode */}
      {onBackToOrganizations && (
        <div className="mb-4">
          <button
            onClick={onBackToOrganizations}
            className="flex items-center text-blue-600 hover:text-blue-800 transition-colors"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Organizations
          </button>
        </div>
      )}

      {/* Organization Information */}
      <div className="bg-white rounded-lg shadow p-6">
        {canEditOrgDetails && (
          <div className="flex items-center justify-between mb-6">
            <HeadlineMedium color="gray-900">Edit Organization</HeadlineMedium>
            <button
              onClick={handleSaveOrganization}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors min-h-[44px]"
            >
              {saveButtonText}
            </button>
          </div>
        )}

        {canEditOrgDetails ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Organization Name */}
            <div className="md:col-span-2">
              <label htmlFor="org-name-edit" className="block">
                <LabelMedium color="gray-700">Name *</LabelMedium>
              </label>
              <input
                id="org-name-edit"
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter organization name"
              />
            </div>

            {/* Address Fields */}
            <div className="md:col-span-2">
              <LabelMedium color="gray-700">Street Address</LabelMedium>
              <input
                type="text"
                value={formData.street}
                onChange={(e) => handleInputChange("street", e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Street address"
              />
            </div>

            <div>
              <LabelMedium color="gray-700">City *</LabelMedium>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => handleInputChange("city", e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="City"
              />
            </div>

            <div>
              <LabelMedium color="gray-700">State *</LabelMedium>
              <input
                type="text"
                value={formData.state}
                onChange={(e) => handleInputChange("state", e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="State"
              />
            </div>

            <div>
              <LabelMedium color="gray-700">Zip Code</LabelMedium>
              <input
                type="text"
                value={formData.zipCode}
                onChange={(e) => handleInputChange("zipCode", e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Zip code"
              />
            </div>

            <div>
              <LabelMedium color="gray-700">Annual Subscription</LabelMedium>
              <input
                type="text"
                value={formData.annualSubscription}
                onChange={(e) =>
                  handleInputChange("annualSubscription", e.target.value)
                }
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="$1995"
              />
            </div>

            <div>
              <LabelMedium color="gray-700">Subscription Renewal</LabelMedium>
              <input
                type="date"
                value={formData.subscriptionRenewal}
                onChange={(e) =>
                  handleInputChange("subscriptionRenewal", e.target.value)
                }
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <ReadOnlyField id="org-name" label="Name" value={formData.name} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <ReadOnlyField
                  id="org-street"
                  label="Street Address"
                  value={formData.street}
                />
              </div>
              <ReadOnlyField id="org-city" label="City" value={formData.city} />
              <ReadOnlyField
                id="org-state"
                label="State"
                value={formData.state}
              />
              <ReadOnlyField
                id="org-zip"
                label="Zip Code"
                value={formData.zipCode}
              />
            </div>
          </div>
        )}
      </div>

      {organizationId && isAdmin(userRole) && (
        <AccessSection
          formData={formData}
          onInputChange={handleInputChange}
          onSave={handleSaveAccess}
          saving={saving}
          signupLink={generateSignupLink()}
          onCopySignupLink={handleCopySignupLink}
        />
      )}

      {/* Administrators — visible to all org admins; edit/delete superadmin only */}
      {organizationId && isAdmin(userRole) && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <HeadlineMedium color="gray-900">Administrators</HeadlineMedium>
            <button
              type="button"
              onClick={handleAddAdminClick}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors min-h-[44px] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
              aria-label="Add new administrator"
            >
              <span className="font-inter text-label-medium text-white">
                Add New Admin
              </span>
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4">
                    <LabelMedium color="gray-600">First Name</LabelMedium>
                  </th>
                  <th className="text-left py-3 px-4">
                    <LabelMedium color="gray-600">Last Name</LabelMedium>
                  </th>
                  <th className="text-left py-3 px-4">
                    <LabelMedium color="gray-600">Email</LabelMedium>
                  </th>
                  {isSuperAdminUser && (
                    <th className="text-right py-3 px-4">
                      <LabelMedium color="gray-600">Actions</LabelMedium>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {adminUsers.map((admin) => (
                  <tr key={admin.id} className="border-b border-gray-100">
                    <td className="py-3 px-4">
                      <BodyMedium color="gray-900">
                        {admin.display_name?.split(" ")[0] ||
                          admin.email.split("@")[0]}
                      </BodyMedium>
                    </td>
                    <td className="py-3 px-4">
                      <BodyMedium color="gray-900">
                        {admin.display_name?.split(" ").slice(1).join(" ") ||
                          ""}
                      </BodyMedium>
                    </td>
                    <td className="py-3 px-4">
                      <BodyMedium color="gray-900">{admin.email}</BodyMedium>
                    </td>
                    {isSuperAdminUser && (
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end space-x-2">
                          {canManageUser(
                            userRole,
                            userOrganizations,
                            admin,
                          ) && (
                            <>
                              <button
                                type="button"
                                onClick={() => handleEditAdminClick(admin)}
                                className="min-h-[44px] min-w-[44px] p-2 text-gray-400 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 rounded"
                                aria-label={`Edit ${admin.display_name || admin.email}`}
                              >
                                <svg
                                  className="w-4 h-4 mx-auto"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  aria-hidden="true"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                  />
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={() => handleRemoveAdmin(admin)}
                                disabled={removingAdminId === admin.id}
                                className="min-h-[44px] min-w-[44px] p-2 text-gray-400 hover:text-red-600 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 rounded"
                                aria-label={`Remove ${admin.display_name || admin.email} as administrator`}
                              >
                                <svg
                                  className="w-4 h-4 mx-auto"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  aria-hidden="true"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}

                {adminUsers.length === 0 && (
                  <tr>
                    <td
                      colSpan={isSuperAdminUser ? 4 : 3}
                      className="py-8 text-center"
                    >
                      <BodyMedium color="gray-500">
                        No administrators found
                      </BodyMedium>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit Admin Modal */}
      {editingAdmin && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-admin-title"
        >
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <HeadlineMedium color="gray-900" id="edit-admin-title">
                Edit Administrator
              </HeadlineMedium>
              <button
                type="button"
                onClick={handleCloseEditAdminModal}
                className="min-h-[44px] min-w-[44px] text-gray-400 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
                aria-label="Close edit administrator dialog"
              >
                <svg
                  className="w-6 h-6 mx-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSaveAdminEdit} className="space-y-4">
              <div>
                <label htmlFor="edit-admin-first-name">
                  <LabelMedium color="gray-700" className="block mb-2">
                    First Name
                  </LabelMedium>
                </label>
                <input
                  id="edit-admin-first-name"
                  type="text"
                  value={editFormData.firstName}
                  onChange={(e) =>
                    handleEditFormChange("firstName", e.target.value)
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label htmlFor="edit-admin-last-name">
                  <LabelMedium color="gray-700" className="block mb-2">
                    Last Name
                  </LabelMedium>
                </label>
                <input
                  id="edit-admin-last-name"
                  type="text"
                  value={editFormData.lastName}
                  onChange={(e) =>
                    handleEditFormChange("lastName", e.target.value)
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label htmlFor="edit-admin-email">
                  <LabelMedium color="gray-700" className="block mb-2">
                    Email Address
                  </LabelMedium>
                </label>
                <input
                  id="edit-admin-email"
                  type="email"
                  value={editingAdmin.email || ""}
                  readOnly
                  className="w-full border border-gray-200 rounded-md px-3 py-2 bg-gray-50 text-gray-500"
                  aria-describedby="edit-admin-email-hint"
                />
                <BodyMedium
                  color="gray-500"
                  className="mt-1"
                  id="edit-admin-email-hint"
                >
                  Email cannot be changed here.
                </BodyMedium>
              </div>

              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <BodyMedium color="gray-700" className="mb-3">
                  Send a new setup email with a fresh password link. Useful if
                  the original invitation expired or was not received.
                </BodyMedium>
                <button
                  type="button"
                  onClick={handleResendAdminInvite}
                  disabled={savingAdminEdit || resendingAdminInvite}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50 min-h-[44px] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                >
                  {resendingAdminInvite
                    ? "Sending invitation..."
                    : "Resend invitation email"}
                </button>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseEditAdminModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 min-h-[44px]"
                  disabled={savingAdminEdit || resendingAdminInvite}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 min-h-[44px]"
                  disabled={savingAdminEdit || resendingAdminInvite}
                >
                  {savingAdminEdit ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Admin Modal */}
      {showAddAdminModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <HeadlineMedium color="gray-900">
                Add New Administrator
              </HeadlineMedium>
              <button
                onClick={handleCloseAdminModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <form onSubmit={handleInviteAdmin} className="space-y-4">
              <div>
                <LabelMedium color="gray-700" className="block mb-2">
                  First Name
                </LabelMedium>
                <input
                  type="text"
                  value={adminFormData.firstName}
                  onChange={(e) =>
                    handleAdminFormChange("firstName", e.target.value)
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter first name"
                  required
                />
              </div>

              <div>
                <LabelMedium color="gray-700" className="block mb-2">
                  Last Name
                </LabelMedium>
                <input
                  type="text"
                  value={adminFormData.lastName}
                  onChange={(e) =>
                    handleAdminFormChange("lastName", e.target.value)
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter last name"
                  required
                />
              </div>

              <div>
                <LabelMedium color="gray-700" className="block mb-2">
                  Email Address
                </LabelMedium>
                <input
                  type="email"
                  value={adminFormData.email}
                  onChange={(e) =>
                    handleAdminFormChange("email", e.target.value)
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter email address"
                  required
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <BodyMedium color="blue-800">
                  An invitation email will be sent to this address with
                  instructions to set up their administrator account.
                </BodyMedium>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseAdminModal}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  disabled={invitingAdmin}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={invitingAdmin}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {invitingAdmin ? "Sending..." : "Send Invitation"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminAccount;
